import * as z from "zod";
import { type Caller, cursorPageOf, cursorPagination, flag, listCountDto, type Result, version } from "../index";
import { erpFileDepositStatuses, erpFileKinds, erpFileStatuses } from "./enums";

// The channel of the files to the ERP while their import on Business Central is by hand (spec v0.3 §7.5, C-49,
// ADR-258): the domains write the files (the items, ADR-257; the sales and the supplier invoices, ADR-256) and hand
// them to integration, which deposits each in the shared folder of its company and keeps the queue of the files to
// import. Who imports records the outcome here, row by row; the domain of the file hears it as a fact of integration,
// as it will hear the outcome TIL brings once the import is automated (D-23). Internal users only.

/** The queue of the files to import, a file and its download: who imports, Finance, Operations (§7.5). */
export const ERP_FILE_QUEUE = "erp_file.queue";
/** Recording how the import of a file went: the role "ERP import" (§7.5) and the administrator. */
export const ERP_FILE_SETTLE = "erp_file.settle";
/** Held only by the system Actor of the domain that hands a file over: no role may hold it. */
export const ERP_FILE_HAND_OVER = "erp_file.hand_over";

/** The rows of a file at most: the file of the items stops at 50,000 (ADR-257). */
export const MAX_ERP_FILE_ROWS = 50_000;
/** The text of a file at most, in characters: 50,000 rows of the items at about 400 characters each. */
export const MAX_ERP_FILE_CHARS = 24_000_000;

/** The name a domain gives its file: a name of the folder, never a path. */
const fileName = z
  .string()
  .min(5)
  .max(80)
  .regex(/^[A-Za-z0-9][A-Za-z0-9_.-]*\.csv$/, "a file name ending in .csv");

/**
 * `handOverErpFile`: a file a domain wrote, for the folder of its company and the queue. `key` names the file for
 * the domain (the same key answers the file handed over the first time, ADR-097); the aggregate is the entity of the
 * domain whose outcome the file carries, the one the outcome is routed to.
 */
export const handOverErpFileInput = z.object({
  key: z.string().min(1).max(200),
  kind: z.enum(erpFileKinds),
  legalEntityId: z.uuid(),
  aggregateType: z
    .string()
    .regex(/^(catalog|documents|procurement)\.[a-z_]+$/)
    .max(80),
  aggregateId: z.uuid(),
  name: fileName,
  rows: z.number().int().min(1).max(MAX_ERP_FILE_ROWS),
  content: z.string().min(1).max(MAX_ERP_FILE_CHARS),
});
export type HandOverErpFileInput = z.input<typeof handOverErpFileInput>;

/** The file as the queue holds it: its name may differ from the one asked, when the folder already had that one. */
export const erpFileHandedDto = z.object({
  erpFileId: z.uuid(),
  name: z.string(),
  depositStatus: z.enum(erpFileDepositStatuses),
});
export type ErpFileHanded = z.output<typeof erpFileHandedDto>;

/** The command of `IntegrationService` a domain calls with a file (ADR-097): the entrypoint implements it. */
export interface ErpFileCommands {
  handOverErpFile(caller: Caller, input: HandOverErpFileInput): Promise<Result<ErpFileHanded>>;
}

/** A row Business Central refused: by line of the file, with the column when the log says it. */
export const erpFileRejectedRow = z.object({
  line: z.number().int().min(1),
  column: z.string().trim().min(1).max(80).nullable().default(null),
  reason: z.string().trim().min(1).max(500),
});
export type ErpFileRejectedRow = z.output<typeof erpFileRejectedRow>;

/**
 * The files, by status: the queue (`to_import`) the oldest first, as they are imported (B-122); every other view the
 * latest first. By kind and company.
 */
export const listErpFilesInput = z.object({
  status: z.enum([...erpFileStatuses, "all"]).default("to_import"),
  kind: z.enum(erpFileKinds).optional(),
  legalEntityId: z.uuid().optional(),
  ...cursorPagination({ defaultLimit: 50, maxLimit: 200 }),
});
export type ListErpFilesInput = z.input<typeof listErpFilesInput>;

export const erpFileDto = z
  .object({
    id: z.uuid(),
    kind: z.enum(erpFileKinds),
    legalEntityId: z.uuid(),
    /** The company's code. */
    company: z.string(),
    name: z.string(),
    rows: z.number().int(),
    status: z.enum(erpFileStatuses),
    depositStatus: z.enum(erpFileDepositStatuses),
    /** The folder of the company it went to, or goes to; null while the company has none. */
    folder: z.string().nullable(),
    depositedAt: z.iso.datetime().nullable(),
    /** Why the last attempt to deposit it failed. */
    depositError: z.string().nullable(),
    /** The entity of the domain the file is of: a file of the items, a sales document, an inbound. */
    aggregateType: z.string(),
    aggregateId: z.uuid(),
    createdAt: z.iso.datetime(),
    /** When who imports and Finance heard it was still waiting past the threshold (§7.5). */
    alertedAt: z.iso.datetime().nullable(),
    settledAt: z.iso.datetime().nullable(),
    version: z.number().int(),
  })
  .meta({ id: "ErpFile" });
export type ErpFile = z.output<typeof erpFileDto>;

export const erpFilePageDto = cursorPageOf(erpFileDto).meta({ id: "ErpFilePage" });

export const erpFileParams = z.object({ erpFileId: z.uuid() });
export type ErpFileParams = z.input<typeof erpFileParams>;

/**
 * A file with its outcome: the rows Business Central refused (up to one per row of the file, so the list leaves them
 * out) and the log who imported it attached, if any.
 */
export const erpFileDetailDto = erpFileDto
  .extend({ rejected: z.array(erpFileRejectedRow), bcLog: z.string().nullable() })
  .meta({ id: "ErpFileDetail" });

/**
 * How the import of a file went (§7.5): every row Business Central refused, with the reason; none, the whole file was
 * imported. `bcLog` is the log of the import as who imported it attached it. Once: a file with an outcome takes no
 * other.
 */
export const settleErpFileBody = z.object({
  version,
  rejected: z.array(erpFileRejectedRow).max(MAX_ERP_FILE_ROWS).default([]),
  bcLog: z.string().max(1_000_000).nullable().default(null),
});
export const settleErpFileInput = erpFileParams.extend(settleErpFileBody.shape);
export type SettleErpFileInput = z.input<typeof settleErpFileInput>;

/** How many files wait for who imports them, and since when: the queue of the home (ADR-086). */
export const countErpFilesInput = z.object({
  /** Only the files whose alert left: past the threshold of `integration.erp_file_overdue_working_days`. */
  overdue: flag.optional(),
});
export type CountErpFilesInput = z.input<typeof countErpFilesInput>;
export const erpFileCountDto = listCountDto;

/** Codes of the routes of the queue. */
export type ErpFileErrorCode = "erp_file_settled" | "erp_file_invalid";

// TODO(V-21): T-82 asks whether Business Central gives the outcome of an import row by row; until it answers, the log
// is read as one row per line: the line of the file, then the column if any, then the reason, separated by `;` or a
// tab, or `Line <n>: <reason>`. A line the reader does not understand is named, never guessed.
const LOG_LINE = /^\s*(?:line|row|riga)?\s*(\d+)\s*(?:[;\t:,]\s*)(.*)$/i;

/**
 * The rows the log of an import names, from the text who imports pastes or loads (§7.5): each with its line, column
 * and reason, once. The lines of the log past the file, and those the reader does not understand, are named apart.
 */
export function parseBcImportLog(
  log: string,
  rows: number,
): { rejected: ErpFileRejectedRow[]; unread: number[]; outside: number[] } {
  const rejected = new Map<number, ErpFileRejectedRow>();
  const unread: number[] = [];
  const outside: number[] = [];
  for (const [i, text] of log.split(/\r?\n/).entries()) {
    if (text.trim() === "") continue;
    const match = LOG_LINE.exec(text);
    const line = match ? Number(match[1]) : Number.NaN;
    const [first = "", ...others] = (match?.[2] ?? "").split(/[;\t]/).map((part) => part.trim());
    const reason = (others.length > 0 ? others.join("; ") : first).slice(0, 500);
    if (!match || reason === "") unread.push(i + 1);
    else if (line < 1 || line > rows) outside.push(i + 1);
    else if (!rejected.has(line)) {
      rejected.set(line, { line, column: others.length > 0 && first !== "" ? first.slice(0, 80) : null, reason });
    }
  }
  return { rejected: [...rejected.values()].sort((a, b) => a.line - b.line), unread, outside };
}
